import { Award, Flame, Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { fetchUserBadges, fetchUserStreaks } from '../services/userService'
import { Card, CardContent } from './ui/card'

// Split into two components so the streak stat card can sit inline with other
// profile stat cards while the badges strip keeps its own row.

function useStreakData(userId) {
  const [streaks, setStreaks] = useState(null)
  const [badges, setBadges] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    Promise.all([
      fetchUserStreaks(userId).catch(() => ({ all_time_best: 0, per_bracket: [] })),
      fetchUserBadges(userId).catch(() => []),
    ]).then(([s, b]) => {
      if (cancelled) return
      setStreaks(s)
      setBadges(b)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [userId])

  return { streaks, badges, loading }
}

export function StreakCard({ userId }) {
  const { streaks, loading } = useStreakData(userId)
  if (loading) return null
  const best = streaks?.all_time_best || 0
  return (
    <Card glassVariant="orange">
      <CardContent className="p-2.5 sm:p-3 flex flex-col h-full">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
          <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--orange-9)]" />
          <p className="text-sm sm:text-base font-bold text-[var(--orange-12)]">Streak</p>
        </div>
        <p className="hidden sm:block text-xs text-muted-foreground font-body mb-2">
          Your best streak is
        </p>
        <p className="text-3xl sm:text-2xl font-bold text-[var(--orange-12)] mt-1 sm:mt-0">{best}</p>
        <p className="text-[11px] sm:text-xs text-muted-foreground font-body mt-auto pt-1">
          <span className="font-bold">correct</span> picks in a row
        </p>
      </CardContent>
    </Card>
  )
}

export function BadgesStrip({ userId }) {
  const { badges, loading } = useStreakData(userId)
  if (loading || badges.length === 0) return null

  // Collapse duplicates: one pill per threshold with a count, descending so the
  // biggest accomplishments lead. Each (threshold → count) is derived from
  // per-bracket badge rows.
  const grouped = badges.reduce((acc, badge) => {
    const key = `${badge.badge_type}-${badge.threshold}`
    if (!acc[key]) {
      acc[key] = { badge_type: badge.badge_type, threshold: badge.threshold, count: 0 }
    }
    acc[key].count += 1
    return acc
  }, {})
  const summary = Object.values(grouped).sort((a, b) => b.threshold - a.threshold)

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-4 w-4 text-[var(--color-crown)]" />
        <h3 className="font-semibold">Badges</h3>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="badges-strip">
        {summary.map((item, index) => (
          <motion.div
            key={`${item.badge_type}-${item.threshold}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05, type: 'spring', stiffness: 260, damping: 20 }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--orange-6)] bg-[var(--orange-3)]"
            aria-label={`${item.threshold}-pick streak badge earned ${item.count} time${item.count === 1 ? '' : 's'}`}
          >
            <Award className="h-4 w-4 text-[var(--orange-11)]" />
            <span className="text-xs font-medium text-[var(--orange-12)]">
              {item.threshold}-streak
            </span>
            {item.count > 1 && (
              <span className="text-[10px] font-bold text-[var(--orange-11)] bg-[var(--orange-4)] border border-[var(--orange-6)] rounded-full px-1.5 py-0.5 leading-none">
                ×{item.count}
              </span>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  )
}
